import CryptoJS from 'crypto-js'
import service from '@/services'
import Long from 'long'
import moment from 'moment'
import { currencyConfigMap } from '@/constants/currency'
import { useGlobalStore, useUserStore, i18n } from '@/stores'
import { CurrencyFormatOptions } from './types'

/**
 * 获取url对象方法
 * @param url 
 * @param base 
 * @returns 
 */
export function getUrl(url: string, base?: string) {
    const { uploadUrl } = service.config
    return new URL(url, base ?? uploadUrl)
}

// 获取商品首图
export function getFirstImage(url: string) {
    const images = url.split(',').map((path) => getFileUrl(path))
    return images[0] ?? ''
}

/**
 * 获取文件链接地址
 * @param filePath 
 */
export function getFileUrl(filePath?: string) {
    if (filePath) {
        const url = getUrl(filePath)
        return url.href
    }
    return ''
}

/**
 * 获取html文件地址
 * @param fileName 
 * @returns 
 */
export function getHtmlFileUrl(fileName: string) {
    const { mobileOpenUrl } = service.config
    return mobileOpenUrl + '/html/' + fileName
}

/**
 * 获取资讯分享链接
 * @param filePath 
 */
export function getNewsShareUrl(id: number | string = 0) {
    const { shareUrl } = service.config
    return shareUrl + '/#/share/news?id=' + id
}

/**
 * 格式化html字符串
 * @param text 
 * @returns 
 */
export function formatHtmlString(text?: string) {
    if (text) {
        const html = text.toString().replace(/[\n\r]/g, '<br />')
        return html.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/gi, (match, capture) => {
            // 替换img标签src地址为绝对路径
            const url = getUrl(capture)
            return `<img src="${url.href}"  alt=""/>`
        })
    }
    return ''
}

/**
 * 获取html中的图片链接
 * @param text 
 * @returns 
 */
export function getImageSrc(text?: string) {
    const images = text?.match(/<img.*?>/g)
    const urls = images?.reduce((res, img) => {
        const src = img.match(/\ssrc=['"](.*?)['"]/)
        if (src) {
            const url = getUrl(src[1])
            res.push(url.href)
        }
        return res
    }, [] as string[])
    return urls ?? []
}

/**
 * 处理价格颜色的显示
 */
export function handlePriceColor(curValue: number, preValue = 0) {
    if (!curValue || curValue === preValue) {
        return ''
    } else if (curValue > preValue) {
        return 'g-price-up'
    } else {
        return 'g-price-down'
    }
}

/**
 * 处理空值的情况
 * @param value 值
 * @param suffix 后缀名
 * @returns 
 */
export function handleNoneValue<T>(value?: T, suffix = '') {
    if (value == null || String(value) === '') {
        return '--'
    }
    return value + suffix
}

/**
 * 处理0值的情况
 * @param value 值
 * @param suffix 后缀名
 * @returns 
 */
export function handleNumberValue(value: number | string = 0, suffix = '') {
    try {
        if (Number(value) === 0) {
            return '--'
        }
        return value + suffix
    }
    catch {
        return '--'
    }
}

/**
 * 处理表单提交超过16位数字精度丢失的问题
 * @param value 
 * @returns 
 */
export function handleRequestBigNumber(value: string) {
    const globalStore = useGlobalStore()
    if (globalStore.getSystemInfo('tradeChannel') === 'ws') {
        return Long.fromString(value.toString())
    }
    return value
}

/**
 * 数值转换百分比
 * @param value 
 * @param decimal 保留小数位
 */
export function parsePercent(value = 0, decimal = 2, round = false) {
    if (Number.isNaN(value)) {
        return '0%'
    }
    const val = formatDecimal(value * 100, decimal, round)
    return val + '%'
}

/**
 * 四舍五入，处理计算精度问题
 * @param value 
 * @param precision 
 * @returns 
 */
export function round(value: number, precision = 2) {
    // const power = Math.pow(10, precision)
    // const roundedValue = ~~(value * power)
    // return roundedValue / power
    return Number(value.toFixed(precision))
}

/**
 * 乘方函数，处理计算精度问题
 * @param base 
 * @param exponent 
 * @returns 
 */
export function pow(base: number, exponent: number) {
    let result = 1;
    for (let i = 0; i < Math.abs(exponent); i++) {
        result *= base
    }
    if (exponent < 0) {
        return 1 / result
    }
    return result
}

/**
 * 强制保留小数位，不足位数自动补零
 * @param value 
 * @param decimal 保留小数位
 * @param round 是否四舍五入
 * @returns 
 */
export function formatDecimal(value: number | string, decimals = 2, round = true) {
    const num = Number(value)
    if (decimals === 0) {
        // 如果不需要四舍五入，则直接截断（向下取整）
        return round ? Math.round(num).toString() : Math.floor(num).toString()
    }
    if (round) {
        return num.toFixed(decimals)
    }
    const factor = Math.pow(10, decimals)
    const truncatedNum = Math.floor(num * factor) / factor
    const [integerPart, decimalPart = ''] = truncatedNum.toString().split('.')
    // 补全小数部分到指定位数
    const zerosToAdd = decimals - decimalPart.length
    return `${integerPart}.${decimalPart}${zerosToAdd > 0 ? '0'.repeat(zerosToAdd) : ''}`
}

/**
 * 数字转千位符金额
 * @param value 金额数值
 * @param decimal 保留小数位 默认2 
 */
export function formatAmount(value: number, decimal = 2) {
    const reg = new RegExp('(\\d)(?=(\\d{3})+$)', 'ig')
    const result = value.toFixed(decimal).toString().split('.')
    result[0] = result[0].replace(reg, '$1,')
    return result.join('.')
}

/**
 * 日期格式化
 * @param value 
 * @param format 
 * @returns 
 */
export function formatDate(value?: string, format = 'YYYY-MM-DD HH:mm:ss') {
    if (value) {
        return moment(value).format(format)
    }
    return handleNoneValue()
}

/**
 * 获取市场名称
 * @param value 
 * @param format 
 * @returns 
 */
export function displayname(item?: Model.Marketsectionconfignew) {
    switch (i18n.global.locale) {
        case 'zh-CN':
            return item?.displayname ?? ''
        case 'en-US':
            return item?.displaynameen ?? ''
        case 'zh-TW':
            return item?.displaynamezh_tw ?? ''
        case 'vi':
            return item?.displaynamevi ?? ''
        default:
            return item?.displaynameth ?? ''
    }
}

/**
 * 计算两者之间的天数
 * @param value 
 * @returns 
 */
export function diffDays(value?: string) {
    const end = moment(new Date().toISOString())
    const start = moment(value)
    return end.diff(start, 'days')
}

/**
 * 显示公告日期
 * @param value 
 * @returns 
 */
export function formatMsgDate(value?: string) {
    if (moment(value).isSame(moment(), "day")) {
        return formatDate(value, 'HH:mm:ss')
    } else if (moment(value).isSame(moment().subtract(1, 'day'), "day")) {
        return formatDate(value, '昨日 ' + 'HH:mm:ss')
    } else {
        return formatDate(value, 'YYYY-MM-DDs')
    }
}

/**
 * 更换数字单位
 * @param value
 * @returns
 */
export function changeUnit(value: number) {
    const k = 1000
    const sizes = ['', 'K', 'M', 'B']

    if (value > k) {
        /**
         * Math.floor() 不为整数数字【向下取整】
         * Math.log() 取数字的对数 通常为Math.log(y)/Math.log(x) 取以x为底y的对数
         * Math.pow(x,y) 取x为底数的y次幂
         */
        const i = Math.floor(Math.log(value) / Math.log(k))
        const num = ((value / Math.pow(k, i)))
        const unit = sizes[i]
        return (Math.floor(num * 100) / 100).toString() + unit
    } else {
        return (Math.floor(value * 100) / 100).toString()
    }
}

/**
 * Des密钥
 * 手机发送验证码时加密方法
 * @param mobile
 */
export function encryptDesMobile(mobile: string) {
    const hexKey = CryptoJS.enc.Utf8.parse('Ub$kl^P3')
    const hexData = CryptoJS.enc.Utf8.parse(mobile)
    const desData = CryptoJS.DES.encrypt(hexData, hexKey, { iv: hexKey })
    const encrypted = CryptoJS.enc.Base64.stringify(desData.ciphertext)
    return encrypted
}

/**
 * 获取加密后的手机号码
 * @param mobile 手机号码
 * @returns 加密后的手机号码
 */
export function getEncryptMobile(mobile: string) {
    const encryptMobile = encryptDesMobile(mobile)
    return encryptMobile.replace(new RegExp('\\+', 'g'), '*').replace(new RegExp('\\/', 'g'), '-').replace(new RegExp('\\=', 'g'), '.')
}

/**
 * 版本号转数值
 * @param value 
 * @returns 
 */
export function versionToNumber(value: number | string) {
    if (value) {
        const num = value.toString().split(/\D/)
        // 版本号位数
        const place = ['', '0', '00', '000', '0000', '00000', '000000'].reverse()
        for (let i = 0; i < num.length; i++) {
            const len = num[i].length
            num[i] = place[len] + num[i]
        }
        return +num.join('')
    }
    return 0
}

/**
 * 对手方显示脱敏
 */
export function tmMatchAccountID(val: string | number) {
    return val.toString().replace(/^(.{3}).*(.{2})$/, "$1****$2");
}

/**
 * 数组按照特定顺序排序
 * @param arr 
 * @param prop 
 * @param sortValue 
 * @returns 
 */
export function sortBy<T extends object, K extends keyof T>(arr: Array<T>, sortValue: T[K][], prop: K) {
    return arr.sort((a, b) => {
        const aIndex = sortValue.indexOf(a[prop])
        const bIndex = sortValue.indexOf(b[prop])

        // 如果a和b都在sortBy中，按在sortBy中的索引排序
        if (aIndex > -1 && bIndex > -1) {
            return aIndex - bIndex
        }
        // 如果a在sortBy中但b不在，a应该在b之前
        if (aIndex > -1) {
            return -1
        }
        // 如果b在sortBy中但a不在，a应该在b之后
        if (bIndex > -1) {
            return 1
        }
        // 如果a和b都不在sortBy中，它们的位置不应该改变
        return 0
    })
}

// 身份证号 获取对应的证件年龄
export function getIdCardAge(cardnum: string) {
    /// 默认 18-60
    const { getSystemParamValue } = useUserStore()
    const maxAge = getSystemParamValue('325') ?? '60'
    const minAge = 18
    // 根据身份证号提取出年 月 日 
    const idYear = Number(cardnum.substring(6, 10))
    const idMonth = Number(cardnum.substring(10, 12))
    const idDay = Number(cardnum.substring(12, 14))
    // 获取当前日期的年 月 日 
    const year = (new Date()).getFullYear()
    const month = (new Date()).getMonth() + 1
    const day = (new Date()).getDate()
    // 计算年龄 
    let age = year - idYear
    // 需要根据月份和具体日子判断下 
    if ((idMonth > month) || idMonth == month && idDay > day) { age-- }
    return age < minAge || age > Number(maxAge)
}

// 金额转大写
export function numberToChinese(num: undefined | string) {
    /// 数据错误
    if (num === null) { return '' }

    const chineseNums = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
    const unitSection = ['', '拾', '佰', '仟'];
    const bigSection = ['', '万', '亿'];

    // 将金额转换为字符串并确保去除前导零
    let amountStr = String(num).replace(/^0*(\d+)/, '$1')

    // 处理小数部分（可选）
    let decimalPart = ''
    if (amountStr.includes('.')) {
        const parts = amountStr.split('.')
        amountStr = parts[0]
        decimalPart = '.' + parts[1].padEnd(2, '0') // 假设最多两位小数
    }

    // 转换小数部分
    let decimalPartR = ''
    for (let i = 1; i < decimalPart.length; i++) {
        decimalPartR += chineseNums[parseInt(decimalPart[i])] + (i === 1 ? '角' : '分')
    }
    decimalPart = decimalPartR

    // 遍历整数部分进行转换
    let result = '';
    for (let i = amountStr.length - 1; i >= 0; i--) {
        result = chineseNums[parseInt(amountStr[i])] + unitSection[amountStr.length - 1 - i] + result
        // 在万和亿的分隔处添加单位
        if ((amountStr.length - i - 1) % 4 === 0 && i !== 0) {
            result = bigSection[(amountStr.length - i - 1) / 4] + result
        }
    }

    // 去除末尾的零
    result = result.replace(/零+$/, '零')
    // 添加分隔符“元”和小数部分的大写
    result = result.replace(/亿万/, '亿零') + '元' + decimalPart

    return result.trim()
}

/**
 * 货币金额格式化
 * @param value 
 * @param options 
 * @returns 
 */
export function currencyFormat(value = 0, currencyId = 1, options: CurrencyFormatOptions = {}) {
    if (options.noneValue && value <= 0) {
        return options.noneValueText ?? '--'
    }

    const currencyConfig = currencyConfigMap.get(currencyId)

    if (currencyConfig) {
        //const bigInt = BigInt(value)

        const hasValidThreshold = currencyConfig.threshold ? (value > currencyConfig.threshold) : true // 超过有效阈值将启用紧凑格式化

        const intl = new Intl.NumberFormat(currencyConfig.locale, {
            style: options.showSymbol ? 'currency' : 'decimal',
            currency: currencyConfig.currency,
            notation: hasValidThreshold ? (currencyConfig.notation ?? 'standard') : 'standard',
            minimumFractionDigits: options.fractionDigits ?? 2,
            maximumFractionDigits: options.fractionDigits ?? 2
        })

        return intl.format(value)
    }

    return value.toString()
}